import {
  IHouseStepFinishConfig,
  IWizardHouseState,
  TStepPayload,
  TWizardStepComponentEditable,
  WIZARD_HOUSE_STEPS_PATHS
} from "../../../interfaces";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import styled, { css } from "styled-components/macro";
import { Switch } from "antd";
import { Check as CheckIcon } from "@styled-icons/bootstrap";
import { ClockIcon } from "../../../../Icons/Clock";
import { useContextHospi } from "../../../../../context/ContextHospi";
import { useTranslation } from "react-i18next";
import { HouseAPI } from "../../../../../API/houseApi";
import { useNotifier } from "../../../../../context/ContextNotifier";
import { parseServerDateTime } from "../../../../../utils/time";
import format from "date-fns/format";

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;
const Card = styled.div`
  padding: 24px 32px;
  border-radius: 8px;
  background: white;

  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
  }
`;
const CardLeftSide = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;
const CardIconRoot = styled.div<{ $status: "completed" | "pending" }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(p) =>
    p.$status === "pending"
      ? css`
          background: #f8ece7;
        `
      : ""};

  ${(p) =>
    p.$status === "completed"
      ? css`
          background: #ebf8e7;
        `
      : ""}
`;

const Check = styled(CheckIcon)`
  width: 30px;
  height: 30px;

  path {
    fill: #32c51a;
  }
`;

const Clock = styled(ClockIcon)`
  path {
    fill: ${(p) => p.theme.colors.primary};
  }
`;

const CardIcon = ({ status }: { status: "completed" | "pending" }) => {
  return (
    <CardIconRoot $status={status}>
      {status === "pending" ? <Clock /> : null}
      {status === "completed" ? <Check /> : null}
    </CardIconRoot>
  );
};
const CardStatus = styled.div`
  font-size: 13px;
  color: ${(p) => p.theme.colors.midGrey};
`;
const CardTitle = styled.div`
  font-size: 18px;
  color: ${(p) => p.theme.colors.hardGray};
`;
const CardStatusAndTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardRightSide = styled.div`
  color: ${(p) => p.theme.colors.midGrey};
  display: flex;
  align-items: center;
  gap: 8px;
`;
const StarNote = styled.div`
  margin-top: 8px;
  line-height: 21px;
  color: ${(p) => p.theme.colors.midGrey};
`;

const statePath = WIZARD_HOUSE_STEPS_PATHS.FinishConfig;

export const WizardStepFinishConfig: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IHouseStepFinishConfig>>(
      state[statePath] ?? {}
    );
    const { userProfile } = useContextHospi();
    const notifier = useNotifier();

    const { t } = useTranslation("house");

    useImperativeHandle(ref, () => ({
      save: () => {
        return HouseAPI.wizardSaveFinishConfig(
          state.house_id!,
          state.wizard_type!,
          {
            ...values,
            host_verified: userProfile?.verification_status === "verified"
          }
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            notifier.error({
              message: e.toString()
            });
            return Promise.reject(e);
          });
      }
    }));

    return (
      <Root>
        <Card>
          <CardLeftSide>
            <CardIcon
              status={
                userProfile?.intake_data ||
                userProfile?.verification_status === "verified"
                  ? "completed"
                  : "pending"
              }
            />
            <CardStatusAndTitleContainer>
              <CardStatus>
                {t(`wizard.steps.FinishConfig.status.planned`)}
              </CardStatus>
              <CardTitle>
                {t(`wizard.steps.FinishConfig.intake.title`)}
              </CardTitle>
            </CardStatusAndTitleContainer>
          </CardLeftSide>
          <CardRightSide>
            {userProfile?.intake_data ? (
              <>
                {t(`wizard.steps.FinishConfig.intake.status_done`, {
                  date: format(
                    parseServerDateTime(userProfile.intake_data.start_time),
                    "MMM do"
                  ),
                  time: format(
                    parseServerDateTime(userProfile.intake_data.start_time),
                    "kk:mm"
                  )
                })}
              </>
            ) : (
              t(`wizard.steps.FinishConfig.intake.status_not_planned`)
            )}
          </CardRightSide>
        </Card>
        {userProfile?.verification_status === "verified" ? (
          <Card>
            <CardLeftSide>
              <CardIcon status={"completed"} />
              <CardStatusAndTitleContainer>
                <CardStatus>
                  {t(`wizard.steps.FinishConfig.status.completed`)}
                </CardStatus>
                <CardTitle>
                  {t(`wizard.steps.FinishConfig.verification.title`)}
                </CardTitle>
              </CardStatusAndTitleContainer>
            </CardLeftSide>
            <CardRightSide>
              {t(`wizard.steps.FinishConfig.verification.status_done`)}
            </CardRightSide>
          </Card>
        ) : (
          <Card>
            <CardLeftSide>
              <CardIcon status={"pending"} />
              <CardStatusAndTitleContainer>
                <CardStatus>
                  {t(`wizard.steps.FinishConfig.status.pending`)}
                </CardStatus>
                <CardTitle>
                  {t(`wizard.steps.FinishConfig.verification.title`)}
                </CardTitle>
              </CardStatusAndTitleContainer>
            </CardLeftSide>
            <CardRightSide>
              {t(`wizard.steps.FinishConfig.verification.status_pending`)}
            </CardRightSide>
          </Card>
        )}
        <Card>
          <CardLeftSide>
            <CardIcon status={"pending"} />
            <CardStatusAndTitleContainer>
              <CardStatus>
                {t(`wizard.steps.FinishConfig.status.pending`)}
              </CardStatus>
              <CardTitle>
                {t(`wizard.steps.FinishConfig.auto_publish.title`)}
              </CardTitle>
            </CardStatusAndTitleContainer>
          </CardLeftSide>
          <CardRightSide>
            {t(
              `wizard.steps.FinishConfig.auto_publish.${
                userProfile?.verification_status === "verified"
                  ? "switch_done"
                  : "switch_label"
              }`
            )}
            <Switch
              checked={values.publish_house_after_finish ?? undefined}
              onChange={(checked) => {
                setValues({
                  publish_house_after_finish: checked
                });
              }}
            />
          </CardRightSide>
        </Card>
        <StarNote>
          {t(
            `wizard.steps.FinishConfig.${
              userProfile?.verification_status === "verified"
                ? "star_note_done"
                : "star_note_notdone"
            }`
          )}
        </StarNote>
      </Root>
    );
  });
